import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Calendar, Clock, User, MapPin, Video, Phone, 
  ChevronLeft, ChevronRight, RefreshCw, Plus, X, Check
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const AppointmentScheduler = () => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { toast } = useToast();
  
  const token = localStorage.getItem('token');

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const fetchAvailableSlots = useCallback(async () => {
    try {
      const startDate = formatDate(selectedDate);
      const endDate = formatDate(new Date(selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000));
      
      const res = await fetch(
        `${API}/api/phase47/scheduling/available-slots?start_date=${startDate}&end_date=${endDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate]);

  const fetchMyAppointments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase47/scheduling/my-appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyAppointments(data.appointments || []);
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchAvailableSlots();
    fetchMyAppointments();
  }, [fetchAvailableSlots, fetchMyAppointments]);

  const bookAppointment = async () => {
    if (!selectedSlot) return;
    
    try {
      const res = await fetch(`${API}/api/phase47/scheduling/book`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slot_id: selectedSlot.id,
          visit_type: selectedSlot.visit_type,
          provider_id: selectedSlot.provider?.id,
          provider_name: selectedSlot.provider?.name,
          date: selectedSlot.date,
          time: selectedSlot.time,
          duration_minutes: selectedSlot.duration_minutes,
          location: selectedSlot.location,
          notes: bookingNotes
        })
      });
      
      if (res.ok) {
        toast({
          title: "Appointment Booked!",
          description: `Your ${selectedSlot.visit_type} has been scheduled for ${selectedSlot.date} at ${selectedSlot.time}`
        });
        setShowConfirmation(false);
        setSelectedSlot(null);
        setBookingNotes('');
        fetchAvailableSlots();
        fetchMyAppointments();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to book appointment", variant: "destructive" });
    }
  };

  const cancelAppointment = async (appointmentId) => {
    try {
      const res = await fetch(`${API}/api/phase47/scheduling/cancel/${appointmentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Patient requested cancellation' })
      });
      
      if (res.ok) {
        toast({ title: "Cancelled", description: "Appointment cancelled successfully" });
        fetchMyAppointments();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to cancel appointment", variant: "destructive" });
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  // Group slots by date
  const slotsByDate = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="appointment-scheduler">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="h-7 w-7 text-teal-600" />
            Schedule an Appointment
          </h1>
          <p className="text-gray-600">Book visits with your care team online</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'schedule' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('schedule')}
          className={activeTab === 'schedule' ? 'bg-teal-600' : ''}
        >
          <Plus className="h-4 w-4 mr-2" />
          Book New
        </Button>
        <Button
          variant={activeTab === 'appointments' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('appointments')}
          className={activeTab === 'appointments' ? 'bg-teal-600' : ''}
        >
          <Calendar className="h-4 w-4 mr-2" />
          My Appointments ({myAppointments.length})
        </Button>
      </div>

      {activeTab === 'schedule' && (
        <>
          {/* Week Navigation */}
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
            <Button variant="ghost" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="h-5 w-5" />
              Previous Week
            </Button>
            <span className="font-medium">
              Week of {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <Button variant="ghost" onClick={() => navigateWeek(1)}>
              Next Week
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Available Slots */}
          <div className="space-y-4">
            {Object.keys(slotsByDate).length > 0 ? (
              Object.entries(slotsByDate).map(([date, slots]) => (
                <Card key={date}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">
                      {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { 
                        weekday: 'long', month: 'long', day: 'numeric' 
                      })}
                    </CardTitle>
                    <CardDescription>{slots.length} available slots</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {slots.map((slot) => (
                        <div
                          key={slot.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${
                            selectedSlot?.id === slot.id 
                              ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500' 
                              : 'hover:border-teal-300 hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setSelectedSlot(slot);
                            setShowConfirmation(true);
                          }}
                          data-testid={`slot-${slot.id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-semibold text-lg">{slot.time}</span>
                            <Badge variant="outline" className="text-xs">
                              {slot.duration_minutes} min
                            </Badge>
                          </div>
                          <p className="font-medium text-teal-700">{slot.visit_type}</p>
                          <div className="mt-2 text-sm text-gray-500 space-y-1">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {slot.provider?.name}
                            </div>
                            <div className="flex items-center gap-1">
                              {slot.location === 'Telehealth' ? (
                                <Video className="h-3 w-3" />
                              ) : (
                                <MapPin className="h-3 w-3" />
                              )}
                              {slot.location}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <h3 className="font-semibold text-gray-600">No Available Slots</h3>
                  <p className="text-sm text-gray-500">Try selecting a different week</p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {activeTab === 'appointments' && (
        <div className="space-y-4">
          {myAppointments.length > 0 ? (
            myAppointments.map((apt) => (
              <Card key={apt.id} className="border-l-4 border-l-teal-500">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{apt.visit_type}</h3>
                        <Badge className={apt.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                          {apt.status}
                        </Badge>
                      </div>
                      <p className="text-gray-600">{apt.provider_name}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {apt.scheduled_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {apt.scheduled_time}
                        </span>
                        <span className="flex items-center gap-1">
                          {apt.location === 'Video Call' ? <Video className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                          {apt.location}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {apt.location === 'Video Call' && (
                        <Button className="bg-teal-600 hover:bg-teal-700">
                          <Video className="h-4 w-4 mr-2" />
                          Join Call
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => cancelAppointment(apt.id)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">No Upcoming Appointments</h3>
                <p className="text-sm text-gray-500 mb-4">Schedule your first appointment</p>
                <Button onClick={() => setActiveTab('schedule')} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Book Appointment
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Booking Confirmation Modal */}
      {showConfirmation && selectedSlot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-teal-600" />
                Confirm Appointment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-teal-50 rounded-lg space-y-2">
                <p className="font-semibold text-lg text-teal-900">{selectedSlot.visit_type}</p>
                <p className="text-teal-700">{selectedSlot.provider?.name}</p>
                <div className="flex gap-4 text-sm text-teal-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {selectedSlot.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {selectedSlot.time}
                  </span>
                </div>
                <p className="text-sm text-teal-600 flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {selectedSlot.location}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Notes for the provider (optional)</label>
                <Textarea
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  placeholder="Any specific concerns or topics to discuss..."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={bookAppointment} className="flex-1 bg-teal-600 hover:bg-teal-700">
                  Confirm Booking
                </Button>
                <Button variant="outline" onClick={() => {
                  setShowConfirmation(false);
                  setSelectedSlot(null);
                }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AppointmentScheduler;
